import React, { useEffect, useRef, useState } from 'react'
import ReactPaginate from 'react-paginate'
import labelKey from "../../app/localization/label.json"

interface props {
    totalResult: any
    toResult: any
    ofResult: any
    onChange: any
    pageSize: any
    onPageChange: any
    pageCount: any
    currentPage?: any;
    isLoading?: any
    pageSizeOptions?: Array<number | string>
}

const DEFAULT_PAGE_SIZE_OPTIONS: Array<number | string> = [10, 25, 50, 100, 250, 500, 1000]
const TABLE_SCROLL_CLASS = 'ss-table-inner-scroll'
const TABLE_NATURAL_CLASS = 'ss-table-natural-height'
const COMPACT_FOOTER_MQ = '(max-width: 719.98px)'

const isListingTableWrap = (el: Element): el is HTMLElement => {
    if (!(el instanceof HTMLElement)) return false
    if (!el.classList.contains('table-responsive')) return false
    // Skip nested mini-grids used inside cells / expand rows
    if (el.classList.contains('sticky-responsive')) return false
    if (el.closest('td, th')) return false
    return true
}

const findListingTableWraps = (host: HTMLElement): HTMLElement[] => {
    const scopes: HTMLElement[] = []
    const modalBody = host.closest('.modal-body')
    const card = host.closest('.card')
    const content = host.closest('#kt_content, #kt_post, .content')
    if (modalBody instanceof HTMLElement) scopes.push(modalBody)
    if (card instanceof HTMLElement) scopes.push(card)
    if (host.parentElement instanceof HTMLElement) scopes.push(host.parentElement)
    if (content instanceof HTMLElement) scopes.push(content)

    const collectFromScope = (scope: HTMLElement, requireTableHeight: boolean): HTMLElement[] => {
        const selector = requireTableHeight
            ? '.table-responsive.table-height'
            : '.table-responsive'
        const found: HTMLElement[] = []
        scope.querySelectorAll(selector).forEach((el) => {
            if (!isListingTableWrap(el)) return
            if (!requireTableHeight) el.classList.add('table-height')
            // Prefer wraps that appear before pagination in the DOM
            const pos = host.compareDocumentPosition(el)
            if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return
            found.push(el)
        })
        return found
    }

    for (const requireHeight of [true, false]) {
        for (const scope of scopes) {
            const found = collectFromScope(scope, requireHeight)
            if (found.length) return found
        }
    }

    return []
}

const Pagination = ({
    totalResult,
    toResult,
    ofResult,
    onChange,
    pageSize,
    onPageChange,
    pageCount,
    currentPage,
    isLoading,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: props) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const pageSizeFieldId = React.useId()
    const [isCompactFooter, setIsCompactFooter] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(COMPACT_FOOTER_MQ).matches : false
    )
    const totalResultDisplay = isLoading ? '-' : totalResult || 0;
    const toResultDisplay = isLoading ? '-' : toResult || 0;
    const ofResultDisplay = isLoading ? '-' : ofResult || 0;

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return
        const mq = window.matchMedia(COMPACT_FOOTER_MQ)
        const onChangeMq = () => setIsCompactFooter(mq.matches)
        onChangeMq()
        if (mq.addEventListener) mq.addEventListener('change', onChangeMq)
        else mq.addListener(onChangeMq)
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChangeMq)
            else mq.removeListener(onChangeMq)
        }
    }, [])

    useEffect(() => {
        const host = rootRef.current
        if (!host) return

        const apply = () => {
            const wraps = findListingTableWraps(host)
            const cards = new Set<HTMLElement>()

            wraps.forEach((el) => {
                // Prefer inner-scroll layout so min-height: 55vh remains visible when sparse
                el.classList.add(TABLE_SCROLL_CLASS)
                el.classList.remove(TABLE_NATURAL_CLASS)
                const card = el.closest('.card')
                if (card instanceof HTMLElement) cards.add(card)
            })

            cards.forEach((card) => {
                card.classList.add('ss-listing-card')
            })

            return { wraps, cards }
        }

        let { wraps, cards } = apply()

        // Re-apply after async table mount / filter refresh
        const raf = window.requestAnimationFrame(() => {
            const next = apply()
            wraps = next.wraps
            cards = next.cards
        })

        return () => {
            window.cancelAnimationFrame(raf)
            wraps.forEach((el) => {
                el.classList.remove(TABLE_SCROLL_CLASS)
                el.classList.remove(TABLE_NATURAL_CLASS)
            })
            cards.forEach((card) => card.classList.remove('ss-listing-card'))
        }
    }, [pageSize, ofResult])

    return (
        <div
            ref={rootRef}
            className={`ss-table-pagination row g-0 align-items-center ${isCompactFooter ? 'ss-table-pagination--compact' : ''}`}
        >
            {/* Info - showing X to Y of Z */}
            <div 
                className="ss-table-pagination__info col-6"
                title={`Showing ${totalResultDisplay} to ${toResultDisplay} of ${ofResultDisplay} entries`}
            >
                <>
                    <span>{labelKey.showing}</span>
                    <span className="ss-table-pagination__num">{totalResultDisplay}</span>
                    <span>{labelKey.to}</span>
                    <span className="ss-table-pagination__num">{toResultDisplay}</span>
                    <span>{labelKey.of}</span>
                    <span className="ss-table-pagination__num">{ofResultDisplay}</span>
                    <span>{labelKey.entries}</span>
                </>
            </div>

            {/* Controls - Per Page + Pagination buttons */}
            <div className="ss-table-pagination__controls col-6 d-flex justify-content-end align-items-center">
                {/* Per Page Select */}
                <div className="ss-table-pagination__page-size">
                    {!isCompactFooter && (
                        <label className="ss-table-pagination__label" htmlFor={pageSizeFieldId}>
                            {labelKey.perPage}
                        </label>
                    )}
                    <select
                        id={pageSizeFieldId}
                        value={String(pageSize)}
                        className="ss-table-pagination__select form-select form-select-sm"
                        onChange={onChange}
                        aria-label={labelKey.perPage}
                        title={`${labelKey.perPage}: ${pageSize}`}
                    >
                        {pageSizeOptions.map((size) => (
                            <option key={String(size)} value={String(size)}>
                                {size === -1 || size === 'All' ? 'All' : size}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Pagination Buttons */}
                <ReactPaginate
                    breakLabel={"…"}
                    nextLabel={isCompactFooter ? '›' : 'Next'}
                    previousLabel={isCompactFooter ? '‹' : 'Previous'}
                    onPageChange={(e) => onPageChange(e)}
                    pageRangeDisplayed={isCompactFooter ? 1 : 5}
                    marginPagesDisplayed={1}
                    pageCount={pageCount}
                    containerClassName="pagination"
                    pageClassName="page-item"
                    previousClassName="page-item"
                    nextClassName="page-item"
                    breakClassName="page-item"
                    pageLinkClassName="page-link"
                    previousLinkClassName="page-link"
                    nextLinkClassName="page-link"
                    breakLinkClassName="page-link"
                    activeClassName="active"
                    forcePage={currentPage ? currentPage - 1 : 0}
                />
            </div>
        </div>
    )
}

export default Pagination